// Copyright (c) 2025, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import "@src/App.scss";
import { store } from "@slices/store";
import { Provider } from "react-redux";
import { ThemeMode } from "@utils/types";
import AppHandler from "@app/AppHandler";
import { themeSettings } from "@src/theme";
import { SnackbarProvider } from "notistack";
import AppAuthProvider from "@context/AuthContext";
import { AuthProvider } from "@asgardeo/auth-react";
import { createContext, useState, useMemo } from "react";
import { APP_NAME, AsgardeoConfig } from "@config/config";
import { ThemeProvider, createTheme } from "@mui/material/styles";

export const ColorModeContext = createContext({ toggleColorMode: () => {} });

function App() {
  document.title = APP_NAME;
  const processLocalThemeMode = (): ThemeMode => {
    try {
      const savedTheme = localStorage.getItem("internal-app-theme");
      if (savedTheme === ThemeMode.Light || savedTheme === ThemeMode.Dark) {
        return savedTheme;
      }

      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      const systemTheme = prefersDark ? ThemeMode.Dark : ThemeMode.Light;

      localStorage.setItem("internal-app-theme", systemTheme);
      return systemTheme;
    } catch (err) {
      console.error("Theme detection failed, defaulting to light mode.", err);
      return ThemeMode.Light;
    }
  };

  const [mode, setMode] = useState<ThemeMode>(processLocalThemeMode());

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        localStorage.setItem("internal-app-theme", mode === ThemeMode.Light ? ThemeMode.Dark : ThemeMode.Light);
        setMode((prevMode) => (prevMode === ThemeMode.Light ? ThemeMode.Dark : ThemeMode.Light));
      },
    }),
    [mode]
  );

  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <SnackbarProvider maxSnack={3} preventDuplicate>
        <ThemeProvider theme={theme}>
          <Provider store={store}>
            <AuthProvider config={AsgardeoConfig}>
              <AppAuthProvider>
                <AppHandler />
              </AppAuthProvider>
            </AuthProvider>
          </Provider>
        </ThemeProvider>
      </SnackbarProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
